import { supabase } from "@/integrations/supabase/client";

// Thin typed wrappers around the admin-analytics aggregate RPCs
// (20260612130000_analytics_admin_aggregates.sql and, for issue #872,
// 20261010120000_issue872_contain_org_engagement_records.sql). The cast lives
// in one place here because the generated types.ts is regenerated only after
// a migration is applied. Every function is an admin-gated SECURITY DEFINER
// aggregate and returns no agent identifier and no event timestamp.
//
// Issue #872: these RPCs are the ONLY way the browser learns about engagement.
// The raw event tables (profile_view_events and its four siblings) are
// insert-only for API roles, and agent_org_fit_scores no longer admits org
// admins. src/lib/issue872EngagementPrivacyGuards.test.ts fails the suite if
// browser code reads any of them again.
//
// Organizations see no Fit Score figures at all (20261012135336, founder
// decision 2026-10-12): the fit summary, matched tenure and inbox fit score
// RPCs are gone, and discovery no longer reports markets. Agents' own scores
// and matching are unchanged.
//
// The RPCs and the frontend deploy separately (Supabase migration apply vs.
// Vercel build), so a call can briefly land before a function exists. Callers
// treat an error as "no data yet" and fall back to their empty states.

const rpc = <T,>(fn: string, args?: Record<string, unknown>) =>
  supabase.rpc(fn, args) as unknown as Promise<{
    data: T | null;
    error: { message: string } | null;
  }>;

/**
 * One row of get_org_discovery_summary: distinct outside agents behind an
 * activity. The server omits every row under three agents, and returns nothing
 * at all when fewer than three agents did anything, so "no rows" means
 * "nothing reportable", never "zero". `market` is always null since
 * 20261012135336 (kept in the result type for deploy-order safety).
 */
export interface DiscoverySummaryRow {
  activity: "Any activity" | "Viewed profile" | "Saved profile" | "Compared you";
  market: null;
  agents: number;
}

/**
 * Event count for one activity (get_org_activity_counts). Never 'save' or
 * 'unsave': those are decisions by people and are reported only as floored
 * distinct savers (getOrgSavedCount, getOrgDiscoverySummary).
 */
export interface ActivityCountRow {
  /** 'profile_view', 'profile_view_team', 'comparison', or an agent_interaction_events.event_type other than save/unsave. */
  activity: string;
  events: number;
}

/**
 * The feed banner's platform counters (get_platform_live_activity, #914).
 * Any signed-in caller may read it; a call without a session is 42501.
 */
export interface PlatformLiveActivity {
  /**
   * Distinct agents who viewed a profile on `through_day`. NULL below three
   * (withheld): zero, one and two look the same, so never render it as 0.
   */
  agents_active_last_day: number | null;
  /** Comparisons over the seven completed UTC days ending `through_day`. Event volume, not floored. */
  comparisons_last_7_days: number;
  /** The last completed UTC day counted, "YYYY-MM-DD". */
  through_day: string;
}

export interface ViewSeriesRow {
  /** First UTC date of the bucket, YYYY-MM-DD. */
  bucket_start: string;
  external_views: number;
  team_views: number;
}

export interface TrafficSourceRow {
  traffic_source: string;
  views: number;
}

export interface SectionEngagementRow {
  section_id: string;
  events: number;
}

export interface ComparisonSetRow {
  rival_org_id: string;
  name: string;
  logo_url: string | null;
  comparisons: number;
  /** Integer win-rate %, or null when no comparison in the set was decided. */
  win_rate_pct: number | null;
}

/**
 * Distinct outside agents who have saved the org, through the report cutoff.
 * NULL below three (suppressed): zero, one and two look the same.
 */
export function getOrgSavedCount(orgId: string) {
  return rpc<number | null>("get_org_saved_count", { p_org_id: orgId });
}

/** The first UTC date not yet reportable, "YYYY-MM-DD" (see analytics/types.ts). */
export function getEngagementReportCutoff() {
  return rpc<string>("get_engagement_report_cutoff");
}

/** Platform-wide counters for the feed banner, one row (see PlatformLiveActivity). */
export function getPlatformLiveActivity() {
  return rpc<PlatformLiveActivity[]>("get_platform_live_activity");
}

/** Distinct-agent discovery counts (views / saves / comparisons), floored at three. */
export function getOrgDiscoverySummary(orgId: string, start: string, end: string) {
  return rpc<DiscoverySummaryRow[]>("get_org_discovery_summary", {
    p_org_id: orgId,
    p_start: start,
    p_end: end,
  });
}

/** Event counts by activity over a UTC [start, end) range of completed days. */
export function getOrgActivityCounts(orgId: string, start: string, end: string) {
  return rpc<ActivityCountRow[]>("get_org_activity_counts", {
    p_org_id: orgId,
    p_start: start,
    p_end: end,
  });
}

/** Story views per UTC day / ISO week / month. Empty buckets are omitted. */
export function getOrgProfileViewSeries(
  orgId: string,
  start: string,
  end: string,
  bucket: "day" | "week" | "month",
) {
  return rpc<ViewSeriesRow[]>("get_org_profile_view_series", {
    p_org_id: orgId,
    p_start: start,
    p_end: end,
    p_bucket: bucket,
  });
}

/** External story views per traffic source. */
export function getOrgTrafficSources(orgId: string, start: string, end: string) {
  return rpc<TrafficSourceRow[]>("get_org_traffic_sources", {
    p_org_id: orgId,
    p_start: start,
    p_end: end,
  });
}

/** External story engagement per section. */
export function getOrgSectionEngagement(orgId: string, start: string, end: string) {
  return rpc<SectionEngagementRow[]>("get_org_section_engagement", {
    p_org_id: orgId,
    p_start: start,
    p_end: end,
  });
}

/** Sum of one activity's events in a get_org_activity_counts result. */
export function eventsFor(rows: ActivityCountRow[] | null | undefined, activity: string): number {
  return (rows ?? []).find((r) => r.activity === activity)?.events ?? 0;
}

/** Profile-view activity labels never counted as an agent interaction. */
const NON_INTERACTION = new Set(["profile_view", "profile_view_team", "comparison"]);

/** Total agent_interaction_events in a get_org_activity_counts result. */
export function interactionEvents(rows: ActivityCountRow[] | null | undefined): number {
  return (rows ?? [])
    .filter((r) => !NON_INTERACTION.has(r.activity))
    .reduce((sum, r) => sum + r.events, 0);
}

/**
 * Rivals this org co-appears with in comparisons, already same-brand filtered,
 * k-anonymity floored and capped at 4 rows by the RPC
 * (20260723150000_org_comparison_set_rpc.sql). Returns zero rows when the org
 * is under the card-level comparison floor.
 */
export function getOrgComparisonSet(orgId: string, start: string, end: string) {
  return rpc<ComparisonSetRow[]>("get_org_comparison_set", {
    p_org_id: orgId,
    p_start: start,
    p_end: end,
  });
}
